// @ts-nocheck
// Personalized landing page shown when a reviewer follows their unique
// /r/<token> link. Loads the invitation by token, shows the personal
// letter, and lets the reviewer trigger a Magic Link to claim access.
// On magic-link return, the App.tsx top-level effect sees the stored
// token and calls claim_invitation to attach a reviewer license.

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  DEFAULT_REVIEWER_LETTER,
  expandLetter,
} from "../content/he/reviewer";

const styles = `
.rev-wrap { max-width: 720px; margin: 40px auto; padding: 0 20px 80px; direction: rtl; font-family: 'Heebo', sans-serif; color: #2D1B10; }
.rev-card { background: #FFFDF9; border-radius: 24px; padding: 40px 36px; box-shadow: 0 12px 48px rgba(92,61,46,0.12); border: 1px solid #E8DCC8; }
.rev-chip { display: inline-block; background: rgba(160,112,74,0.12); color: #5C3D2E; padding: 5px 14px; border-radius: 14px; font-size: 12px; font-weight: 600; letter-spacing: 1px; margin-bottom: 14px; }
.rev-title { font-family: 'Frank Ruhl Libre', serif; font-size: 30px; font-weight: 700; color: #5C3D2E; margin-bottom: 6px; line-height: 1.3; }
.rev-subtitle { font-size: 14px; color: #8B7355; margin-bottom: 24px; }
.rev-letter { font-size: 16px; line-height: 1.95; color: #2D1B10; white-space: pre-wrap; padding: 24px 0; border-top: 1px solid #F3EDE3; border-bottom: 1px solid #F3EDE3; margin-bottom: 28px; }
.rev-cta { background: linear-gradient(135deg, #A0704A, #D4875A); color: white; border: none; padding: 14px 32px; border-radius: 16px; font-family: 'Heebo', sans-serif; font-size: 16px; font-weight: 600; cursor: pointer; box-shadow: 0 6px 20px rgba(160,112,74,0.35); transition: transform .15s; }
.rev-cta:hover:not(:disabled) { transform: translateY(-2px); }
.rev-cta:disabled { opacity: .6; cursor: not-allowed; }
.rev-msg { font-size: 14px; line-height: 1.7; padding: 14px 18px; border-radius: 12px; margin-top: 16px; }
.rev-msg.success { background: #E8F4EC; color: #2E7D32; border-right: 3px solid #4CAF50; }
.rev-msg.error { background: #FFF0F0; color: #C62828; border-right: 3px solid #E53935; }
.rev-loading { text-align: center; padding: 80px 20px; color: #8B7355; font-size: 16px; }
.rev-fine { font-size: 13px; color: #8B7355; margin-top: 24px; }
.rev-email { font-family: 'Heebo', monospace; background: #F3EDE3; padding: 2px 8px; border-radius: 6px; direction: ltr; display: inline-block; }
@media (max-width: 600px) { .rev-card { padding: 28px 22px; } .rev-title { font-size: 24px; } .rev-letter { font-size: 15px; } }
`;

const TOKEN_KEY = "pending_invitation_token";
const LAST_SEND_KEY_PREFIX = "rev_last_send_";
const RESEND_COOLDOWN_S = 60; // Supabase's per-identity OTP rate limit

type Props = { token: string };

function readLastSendAt(token: string): number {
  try {
    const raw = window.localStorage.getItem(LAST_SEND_KEY_PREFIX + token);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeLastSendAt(token: string, ts: number) {
  try {
    window.localStorage.setItem(LAST_SEND_KEY_PREFIX + token, String(ts));
  } catch {}
}

export function ReviewerInvitePage({ token }: Props) {
  const [state, setState] = useState<"loading" | "ready" | "sending" | "sent" | "notfound" | "error">(
    "loading"
  );
  const [invitation, setInvitation] = useState<{ name: string; email: string; letter: string; alreadyClaimed: boolean } | null>(
    null
  );
  const [errorMsg, setErrorMsg] = useState("");
  // Cooldown is computed from the persisted last-send-at timestamp so that
  // refreshing the page doesn't reset the timer. `secondsLeft === 0` means
  // resend is allowed.
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  // Bootstrapping: if a previous send happened in this browser and the
  // 60s window is still open, restore the "sent" UI with the remaining
  // cooldown instead of showing the initial "send" button.
  useEffect(() => {
    const last = readLastSendAt(token);
    if (!last) return;
    const elapsed = Math.floor((Date.now() - last) / 1000);
    if (elapsed >= 0 && elapsed < RESEND_COOLDOWN_S) {
      setSecondsLeft(RESEND_COOLDOWN_S - elapsed);
      setState("sent");
    }
  }, [token]);

  // Tick the cooldown every second while it's running.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_invitation_by_token", {
        _token: token,
      });
      if (cancelled) return;
      if (error) {
        setErrorMsg(error.message || "");
        setState("error");
        return;
      }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        setState("notfound");
        return;
      }
      // Stamp the first open on the invitation (fire-and-forget). Chapter 1
      // needs no sign-in, so without this the admin table can't tell an
      // untouched invitation from one that was opened and read.
      supabase.rpc("mark_invitation_opened", { _token: token }).then(
        () => {},
        () => {}
      );
      const letter = expandLetter(
        row.letter_md || DEFAULT_REVIEWER_LETTER,
        row.name
      );
      setInvitation({
        name: row.name,
        email: row.email,
        letter,
        alreadyClaimed: !!row.already_claimed,
        isGift: row.kind === "gift",
      });
      // Only flip to "ready" if we didn't already restore to "sent" above.
      setState((cur) => (cur === "sent" ? "sent" : "ready"));
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSend = async () => {
    if (!invitation) return;
    setState("sending");
    // Remember the token across the magic-link round-trip.
    // localStorage is the same-browser fallback. The redirect URL below is
    // what makes the claim work cross-device: when the recipient reads the
    // magic-link email on their phone (Gmail/Outlook in-app webview), the
    // localStorage from the desktop browser they originally opened the
    // invite in is NOT available — so we encode the token in the redirect
    // URL itself. App.tsx then extracts it from /r/<token> on post-auth.
    try {
      window.localStorage.setItem(TOKEN_KEY, token);
    } catch {}
    const { error } = await supabase.auth.signInWithOtp({
      email: invitation.email,
      options: { emailRedirectTo: `${window.location.origin}/r/${token}` },
    });
    if (error) {
      setErrorMsg(error.message || "שגיאה בשליחת הקישור");
      setState("error");
      return;
    }
    writeLastSendAt(token, Date.now());
    setSecondsLeft(RESEND_COOLDOWN_S);
    setState("sent");
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="rev-wrap">
        {state === "loading" && (
          <div className="rev-loading">טוען את ההזמנה האישית שלכם…</div>
        )}

        {state === "notfound" && (
          <div className="rev-card">
            <div className="rev-title">הקישור לא תקף</div>
            <p className="rev-subtitle">
              יכול להיות שהקישור הועתק חלקית, או שפג תוקפו. נסו לפנות אלינו
              ישירות, או חזרו לקישור מהמייל.
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="rev-card">
            <div className="rev-title">משהו השתבש</div>
            <div className="rev-msg error">{errorMsg || "נסו שוב מאוחר יותר."}</div>
          </div>
        )}

        {(state === "ready" || state === "sending" || state === "sent") &&
          invitation && (
            <div className="rev-card">
              <div className="rev-chip">
                {invitation.isGift ? "🎁 מתנה בשבילך" : "הזמנה לסקירה אישית"}
              </div>
              <h1 className="rev-title">שלום {invitation.name}</h1>
              <div className="rev-subtitle">
                גישה אישית לכל הספר על שם <span className="rev-email">{invitation.email}</span>
              </div>

              <div className="rev-letter">{invitation.letter}</div>

              {state === "sent" ? (
                <>
                  <div className="rev-msg success">
                    <strong>הקישור נשלח אל {invitation.email}.</strong>
                    <br />
                    פתחו את תיבת המייל שלכם וחפשו הודעה מ-<strong>eiforall</strong> (או מ-<strong>noreply@supabase.io</strong>) עם כותרת <em>"קישור הכניסה שלך"</em>.
                    בתוך המייל יש כפתור גדול בצבע חום — לחיצה עליו פותחת את הספר.
                    <br />
                    <br />
                    <strong>לא רואים את המייל?</strong> בדקו בתיקיית הספאם / הקידום. הוא אמור להגיע תוך דקה. אם עדיין לא הגיע, אפשר לבקש שוב למטה.
                  </div>
                  <button
                    className="rev-cta"
                    style={{ marginTop: 18 }}
                    onClick={handleSend}
                    disabled={secondsLeft > 0}
                  >
                    {secondsLeft > 0
                      ? `שלחו לי שוב (${secondsLeft})`
                      : "שלחו לי שוב"}
                  </button>
                  <p className="rev-fine" style={{ marginTop: 10 }}>
                    {secondsLeft > 0
                      ? "מטעמי אבטחה אפשר לבקש קישור חדש כל דקה."
                      : "אם המייל הקודם לא הגיע, לחצו כאן ונשלח חדש."}
                  </p>
                </>
              ) : (
                <>
                  <button
                    className="rev-cta"
                    onClick={handleSend}
                    disabled={state === "sending"}
                  >
                    {state === "sending"
                      ? "שולח קישור התחברות…"
                      : "שלחו לי קישור התחברות"}
                  </button>
                  <p className="rev-fine">
                    {invitation.alreadyClaimed
                      ? "ההזמנה כבר נוצלה בעבר — לא נורא, נשלח לכם קישור התחברות חוזר."
                      : "נשלח קישור חד‑פעמי למייל שלכם, ללא סיסמה."}
                  </p>
                </>
              )}
            </div>
          )}
      </div>
    </>
  );
}
